import { useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MetricCard } from '../metrics/MetricCard';
import { LineChart } from 'lucide-react';
import { MetricCalculationsResult } from '@/hooks/useMetricCalculations';
import { Metric } from '@/types/metrics';

type AggregationLevel = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';


interface MetricDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  capabilityMetrics: Metric[];
  selectedPerformanceMetric?: string;
  metricDimensionId?: string;
  dimensionItemName?: string;
  timeseriesData: any;
  isLoading: boolean;
  aggregation: AggregationLevel;
  currentMetric: Metric | null;
  onCurrentMetricChange: (metric: Metric | null) => void;
  calculations: MetricCalculationsResult | null;
  effectiveTarget?: Array<{ startTs: number; endTs: number; value: number }> | null;
}

function getRelatedMetricIconClass(
  metric: Metric,
  timeseriesData: any,
  aggregation: AggregationLevel
): string {
  if (aggregation !== 'daily') return 'text-[#8884d8]';
  if (!timeseriesData) return 'text-[#8884d8]';

  const apiKey = metric.metricValueKey;
  const series = (timeseriesData as any)[apiKey];

  let currentValue: number | null = null;
  if (Array.isArray(series) && series.length > 0 && Array.isArray(series[0])) {
    const sorted = [...series].sort((a: [number, number], b: [number, number]) => b[0] - a[0]);
    currentValue = typeof sorted[0][1] === 'number' ? sorted[0][1] : null;
  }

  const nowSec = Math.floor(Date.now() / 1000);
  const op = (metric.metricOperation || metric.Operation || '').toLowerCase();
  let cumulativeValue: number | null = null;

  if (Array.isArray(series) && series.length > 0 && Array.isArray(series[0]) && (op === 'average' || op === 'total')) {
    const currentYear = new Date(nowSec * 1000).getFullYear();
    const pointsInYear = (series as [number, number][])
      .filter(([ts, v]) => typeof ts === 'number' && typeof v === 'number' && ts <= nowSec && new Date(ts * 1000).getFullYear() === currentYear);

    if (pointsInYear.length > 0) {
      if (op === 'average') {
        let sum = 0;
        let count = 0;
        for (const [, v] of pointsInYear) {
          sum += v;
          count += 1;
        }
        cumulativeValue = count > 0 ? sum / count : 0;
      } else {
        let sum = 0;
        for (const [, v] of pointsInYear) {
          sum += v;
        }
        cumulativeValue = sum;
      }
    } else {
      cumulativeValue = 0;
    }
  }

  const valueForPercent = cumulativeValue != null ? cumulativeValue : currentValue;

  const currentSeg = Array.isArray(metric.target)
    ? metric.target.find(s => nowSec >= s.startTs && nowSec <= s.endTs)
    : undefined;
  const segTarget = currentSeg?.value ?? null;
  const rawPercent = valueForPercent != null && segTarget && segTarget > 0 ? (valueForPercent / segTarget) * 100 : null;
  // For decreasing targets, convert to rising-target equivalent so alerting thresholds work correctly
  const percent = rawPercent != null && metric.decreasingTarget ? (100 / rawPercent) * 100 : rawPercent;

  const daysLeft = currentSeg ? Math.ceil((currentSeg.endTs - nowSec) / (60 * 60 * 24)) : null;
  const pctThresh = typeof metric.alertingThresholdPercentage === 'number' ? metric.alertingThresholdPercentage : null;
  const durThresh = typeof metric.alertingThresholdDuration === 'number' ? metric.alertingThresholdDuration : null;

  //const zeroPctAlert = percent === 0;
  const thresholdAlert =
    percent != null &&
    pctThresh != null &&
    daysLeft != null &&
    durThresh != null &&
    percent < pctThresh &&
    daysLeft >= 0 &&
    daysLeft < durThresh;

  const isAlert =/* zeroPctAlert ||*/ thresholdAlert;
  return isAlert ? 'text-red-600 animate-pulse' : 'text-[#8884d8]';
}

export function MetricDetailsDialog({ 
  isOpen, onClose, capabilityMetrics, selectedPerformanceMetric, metricDimensionId,
  timeseriesData, isLoading, aggregation, currentMetric, onCurrentMetricChange, calculations,
  effectiveTarget,
}: MetricDetailsDialogProps) {
  // Filter metrics by selected performance metric category and dimension
  const filteredMetrics = capabilityMetrics.filter(metric => {
    // Filter by performance metric category
    const matchesCategory = !selectedPerformanceMetric || 
      selectedPerformanceMetric.toLowerCase() === 'health' ||
      metric.metricCategory?.toLowerCase() === selectedPerformanceMetric.toLowerCase();
    
    // Filter by dimension - if metricDimensionId is provided, only show metrics matching that dimension id
    // If no metricDimensionId, show metrics that don't have any specific dimension (capability metrics)
    const matchesDimension = metricDimensionId
      ? metric.metricDimension?.endsWith(metricDimensionId)
      : !metric.metricDimension;
    
    return matchesCategory && matchesDimension;
  });

  // Set the first metric as current when dialog opens or metrics change
  useEffect(() => {
    if (isOpen) {
      if (filteredMetrics.length > 0 && (!currentMetric || !filteredMetrics.includes(currentMetric))) {
        onCurrentMetricChange(filteredMetrics[0]);
      } else if (filteredMetrics.length === 0) {
        onCurrentMetricChange(null);
      }
    }
  }, [isOpen, filteredMetrics]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle>Metric Details</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-4">
          {filteredMetrics.length === 0 ? (
            <div className="py-4 text-center">
              <p className="text-sm text-muted-foreground">
                No metrics available for the selected category
              </p>
              {selectedPerformanceMetric && selectedPerformanceMetric.toLowerCase() !== 'health' && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Category: {selectedPerformanceMetric}
                </p>
              )}
            </div>
          ) : currentMetric ? (
            <div className="space-y-4">
              <div className="mt-2 flex gap-6">
                <div className="w-3/4">
                  <MetricCard 
                    metric={currentMetric}
                    calculations={calculations!}
                    isLoading={isLoading}
                    timeRange="year"
                    showKPIMode={false}
                    effectiveTarget={effectiveTarget}
                  />
                </div>
                 <div className="w-1/4 border-l border-gray-200 pl-4">
                  <h3 className="text-sm font-medium mb-2">Related Metrics</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                    {filteredMetrics.map((metric, index) => (
                      <div 
                        key={index}
                        onClick={() => {
                          // Update selected metric when clicked
                          if (metric["KPI Name"] !== currentMetric?.["KPI Name"]) {
                            onCurrentMetricChange(metric);
                          }
                        }}
                        className={`p-2 rounded-md text-xs cursor-pointer transition-colors ${currentMetric?.["KPI Name"] === metric["KPI Name"] 
                          ? 'bg-primary/10 border border-primary/30' 
                          : 'hover:bg-muted border border-transparent'}`}
                      >
                          <div className="flex items-center justify-between">
                          <span className="truncate">{metric["KPI Name"]}</span>
                          {metric.metricValueKey && (
                            <LineChart 
                              className={`h-3 w-3 flex-shrink-0 ml-2 ${getRelatedMetricIconClass(metric, timeseriesData, aggregation)}`}
                              aria-label="Has time series data"
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
